import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//Helpers
import { capFirstChar } from 'src/helpers/utilities';
import { bthomeObjectInfos } from 'src/helpers/virtual_helpers/virtual_bthome_helper';
import {
	getBatteryIconStatus,
	getWebIconStatuses,
} from 'src/helpers/virtual_helpers/virtual_icons_helpers';
import { validateGeolocation } from 'src/helpers/geolocalion_helper';
import { celsius_to_fahrenheit, shellyId16 } from 'src/helpers/interface_helpers';
import { convertToUnit, skipUnitConversion } from 'src/helpers/units_helpers';
import { checkForStatisticError } from 'src/components/Chart/chart_helper';
import { getLast24WSRainfallStatus } from './weather_station_statistics_helper';
import { convertWindSpeedFromMs, windSpeedUnitLabel } from 'src/helpers/units/wind_units';
//Stores
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { inputStates } from 'src/stores/features/input-states';
//Types
import type { Device } from 'src/types/Device';
import type { WindSpeedUnit } from 'src/helpers/units/wind_units';
import type { PrecipitationUnit } from 'src/helpers/units/precipitation_unit';

const components = [
	'temperature:0',
	'humidity:0',
	'UV:0',
	'illuminance:0',
	'precipitation:0',
	'moisture_alarm:0',
	'dewpoint:0',
	'direction:0',
	'speed:0',
	'speed:1',
	'pressure:0',
	'devicepower:0',
];

export const weather_station_status_configurator = (
	device: Device,
	status,
	mainLabel = 'temperature',
) => {
	const interface_data = [];
	components.forEach((component) => {
		const _format = get(format);
		const name = component.split(':')[0];
		let unit = name === 'UV' ? name : getSensorUnit(name);
		let rawValue = status[component]?.[getSensorProp(name)];
		let direction = 'N/A';
		let icon = getSensorIcon(name === 'UV' ? 'uv_index' : name);
		let value = rawValue;
		if (name === 'precipitation') {
			unit = device?.ht_ui?.precipitation_unit || 'mm';
			value = getLast24WSRainfallStatus(device, unit as PrecipitationUnit);
		} else if (name === 'dewpoint') {
			unit = device?.ht_ui?.temperature_unit || 'C';
			if (unit === 'F') {
				rawValue = celsius_to_fahrenheit(rawValue);
			}
			value = convertToUnit(rawValue, `°${unit}`);
		} else if (name === 'temperature') {
			unit = device?.ht_ui?.temperature_unit || 'C';
			rawValue = status[component]?.[`t${unit}`] ?? 0;
			value = convertToUnit(rawValue, `°${unit}`);
		} else if (name === 'devicepower') {
			const percent = status[component]?.battery?.percent ?? 'N/A';
			value = `${percent} %`;
			icon = getBatteryIconStatus(percent) as any;
		} else if (checkForStatisticError(value)) {
			rawValue = 'N/A';
			value = `${rawValue} ${unit}`;
		} else if (name === 'direction') {
			const windDirection = getWindDirection(value);
			direction = windDirection.title;
			value = `${value} ${unit} ${direction}`;
			icon.icon = windDirection.icon;
		} else if (name === 'moisture_alarm') {
			value = value
				? _format('rain_status_wet', { default: 'Wet' })
				: _format('rain_status_dry', { default: 'Dry' });
			icon.icon = `fa${value ? 's' : 'r'} fa-droplet`;
		} else if (name === 'speed') {
			const unitKey = (device?.ht_ui?.wind_unit as WindSpeedUnit) || 'ms';
			rawValue = convertWindSpeedFromMs(rawValue, unitKey);
			unit = windSpeedUnitLabel(unitKey);
			value = convertToUnit(rawValue, unit);
		} else {
			value = convertToUnit(value, unit, {
				fixed: 1,
				dontConvert: skipUnitConversion(name),
			});
		}
		const description = capFirstChar(getDescription(component) || icon.title);
		const data = {
			name: component,
			value,
			rawValue,
			unit,
			title: value,
			icon: icon.icon,
			iconColor: icon?.iconColor || null,
			vcName: description,
			translation: description,
			description,
			direction,
			advLabel:
				name !== mainLabel &&
				!/^(temperature|humidity|precipitation|speed|UV):0$/.test(component),
			main: name === mainLabel ? true : undefined,
		};
		interface_data.push(data);
	});
	return interface_data;
};

export const getSensorUnit = (name: string) => {
	const bthomeObj = bthomeObjectInfos.find((i) => i.name == name);
	return bthomeObj?.unit || '';
};

export const getSensorIcon = (name: string) => {
	const webIconStatuses = getWebIconStatuses();
	const bthomeObj = webIconStatuses.find((i) => i.bthome_obj && i.bthome_obj.includes(name));
	return {
		icon: bthomeObj?.icon || '',
		title: bthomeObj?.title || '',
		iconColor: null,
	};
};

const getDescription = (component: string) => {
	const _format = get(format);
	switch (component) {
		case 'speed:1':
			return _format('label_gust_speed', { default: 'Gust speed' });
		case 'moisture_alarm:0':
			return _format('label_rain_status', { default: 'Rain status' });
		case 'devicepower:0':
			return _format('collapse_title_battery', { default: 'Battery' });
		default:
			return '';
	}
};

export const getSensorProp = (name: string) => {
	switch (name) {
		case 'temperature':
			return 'tC';
		case 'illuminance':
			return 'lux';
		case 'humidity':
			return 'rh';
		case 'devicepower':
			return 'battery';
		default:
			return 'value';
	}
};

type WindDirection = {
	title: string;
	icon: string;
};

export const getWindDirection = (degrees: number): WindDirection => {
	degrees = (((degrees + 180) % 360) + 360) % 360;
	const index = Math.floor((degrees + 22.5) / 45) % 8;
	const directions: WindDirection[] = [
		{ title: 'N', icon: 'fas fa-arrow-up' },
		{ title: 'NE', icon: 'fas fa-arrow-up-right' },
		{ title: 'E', icon: 'fas fa-arrow-right' },
		{ title: 'SE', icon: 'fas fa-arrow-down-right' },
		{ title: 'S', icon: 'fas fa-arrow-down' },
		{ title: 'SW', icon: 'fas fa-arrow-down-left' },
		{ title: 'W', icon: 'fas fa-arrow-left' },
		{ title: 'NW', icon: 'fas fa-arrow-up-left' },
	];
	return directions[index];
};

/**
 * Retrieves the location information for a given device. If the device's location settings
 * do not include a time zone, it attempts to retrieve the location from a reporter device
 * associated with the given device. If the reporter is not found or if the geolocation
 * is invalid, default values are returned.
 *
 * @param device
 * @returns The location object containing latitude, longitude, timezone, and elevation.
 */
export const getDeviceLocation = (device: Device): Device['location'] => {
	let locationSettings = device?.location;
	if (!locationSettings?.tz) {
		const reporterID =
			get(inputStates)?.[device.id]?.reporter?.id ||
			devices_status.getByRealId(device.id)?.reporter?.id;
		const reporter: Device = get(devices)[shellyId16(reporterID || '')];
		if (!reporter) {
			return { lat: 0, lng: 0, tz: '', ele: 0, ele_offset: 0 };
		}
		const settings = devices_settings.getByRealId(reporter.id);
		const timeZone = settings?.sys?.location?.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
		let latitude = settings?.sys?.location?.lat;
		let longitude = settings?.sys?.location?.lon;
		if (!validateGeolocation(latitude, longitude)) {
			latitude = 0;
			longitude = 0;
		}
		locationSettings = {
			lat: latitude,
			lng: longitude,
			tz: timeZone,
			ele: 0,
			ele_offset: 0,
		};
	}
	return locationSettings;
};

export const findClosestAngle = (from: number, to: number): number => {
	const delta = ((to - from + 540) % 360) - 180;
	return from + delta;
};
